import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ActivitySelectionScreen from '../components/ActivitySelectionScreen';
import PromptSelectionScreen from '../components/PromptSelectionScreen';
import * as activityTypeService from '../../data/services/activityTypeService';
import * as weeklyPromptService from '../../data/services/weeklyPromptService';
import { useAppContext } from '../../../../shared/context/AppContext';
import './WeeklyPlannerScreen.css';

const SCREEN_ACTIVITY_SELECTION = 'activity_selection';
const SCREEN_PROMPT_SELECTION = 'prompt_selection';

const WeeklyPlannerScreen = () => {
  const navigate = useNavigate();
  const { user, organization, setWeeklyPrompts, dismissWeeklyPlanner } = useAppContext();
  const [currentScreen, setCurrentScreen] = useState(SCREEN_ACTIVITY_SELECTION);
  const [activityTypes, setActivityTypes] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedPromptsByActivity, setSelectedPromptsByActivity] = useState({});
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivityTypes();
  }, []);

  const loadActivityTypes = async () => {
    try {
      setLoading(true);
      const types = await activityTypeService.getActivityTypes();
      setActivityTypes(types);
    } catch (error) {
      console.error('Error loading activity types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityToggle = (activityTypeId) => {
    setSelectedActivities(prev => {
      if (prev.includes(activityTypeId)) {
        return prev.filter(id => id !== activityTypeId);
      } else {
        return [...prev, activityTypeId];
      }
    });
  };

  const handleActivityNext = () => {
    if (selectedActivities.length >= 1 && selectedActivities.length <= 5) {
      setCurrentScreen(SCREEN_PROMPT_SELECTION);
      setCurrentActivityIndex(0);
    }
  };

  const handlePromptToggle = (promptId) => {
    const currentActivityId = selectedActivities[currentActivityIndex];
    setSelectedPromptsByActivity(prev => {
      const currentPrompts = prev[currentActivityId] || [];
      if (currentPrompts.includes(promptId)) {
        return {
          ...prev,
          [currentActivityId]: currentPrompts.filter(id => id !== promptId)
        };
      } else {
        return {
          ...prev,
          [currentActivityId]: [...currentPrompts, promptId]
        };
      }
    });
  };

  const handlePromptNext = () => {
    if (currentActivityIndex < selectedActivities.length - 1) {
      setCurrentActivityIndex(prev => prev + 1);
    } else {
      // Last activity - save and complete
      handleSave();
    }
  };

  const handlePromptBack = () => {
    if (currentActivityIndex > 0) {
      setCurrentActivityIndex(prev => prev - 1);
    } else {
      // Go back to activity selection
      setCurrentScreen(SCREEN_ACTIVITY_SELECTION);
    }
  };

  const handleSave = async () => {
    console.log('💾 WeeklyPlannerScreen.handleSave called');
    try {
      const userId = user?.id || user?.userId;
      console.log('👤 User ID check:', { userId, userKeys: user ? Object.keys(user) : [] });
      
      if (!userId) {
        console.error('❌ User ID not available');
        return;
      }

      // Collect all selected prompt IDs
      const allPromptIds = [];
      selectedActivities.forEach(activityId => {
        const prompts = selectedPromptsByActivity[activityId] || [];
        console.log(`📋 Activity ${activityId} has ${prompts.length} prompts:`, prompts);
        allPromptIds.push(...prompts);
      });

      // Remove duplicates
      const uniquePromptIds = [...new Set(allPromptIds)];
      
      console.log('📊 Collected prompt IDs:', {
        allPromptIds,
        uniquePromptIds,
        selectedActivities,
        selectedPromptsByActivity
      });

      if (uniquePromptIds.length === 0) {
        console.warn('⚠️ No prompts selected, cannot save');
        alert('Please select at least one prompt before saving.');
        return;
      }

      // Save weekly prompts
      // Don't send weekStart/weekEnd - let the server calculate them to ensure consistency
      // This prevents timezone mismatches between client and server
      console.log('🚀 Calling saveWeeklyPrompts API:', {
        promptIds: uniquePromptIds,
        promptIdsCount: uniquePromptIds.length
      });
      
      const result = await weeklyPromptService.saveWeeklyPrompts(uniquePromptIds, null, null);
      
      console.log('✅ Weekly prompts saved successfully:', {
        promptIdsCount: uniquePromptIds.length,
        promptIds: uniquePromptIds,
        weekStart: result?.weekStart,
        savedPrompts: result?.prompts || [],
        result
      });

      // Update context with the saved prompts immediately (use result from save API)
      console.log('🔄 Updating context with saved prompts...');
      let promptsToSet = [];
      
      if (setWeeklyPrompts) {
        // Use the result from save API directly - it's already correct and available
        // The save API returns the exact prompts that were saved with the correct weekStart
        if (result?.prompts && Array.isArray(result.prompts) && result.prompts.length > 0) {
          promptsToSet = result.prompts;
          console.log('📥 Using prompts from save result:', {
            count: promptsToSet.length,
            prompts: promptsToSet,
            weekStart: result.weekStart
          });
          
          // Update context immediately with the saved prompts
          setWeeklyPrompts(promptsToSet);
          console.log('✅ Updated weeklyPrompts in context with', promptsToSet.length, 'prompts');
          
          // Give React time to process the context update
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // DON'T verify by fetching - the save result is already correct
          // Fetching might use a different weekStart calculation and return empty
          console.log('✅ Using save result directly, skipping verification fetch to avoid week mismatch');
        } else {
          console.warn('⚠️ No valid prompts in save result, attempting to fetch...');
          try {
            // Use the weekStart from the save result if available
            const fetchWeekStart = result?.weekStart || null;
            const data = await weeklyPromptService.getCurrentWeekPrompts(fetchWeekStart);
            promptsToSet = data?.prompts || [];
            setWeeklyPrompts(promptsToSet);
            console.log('✅ Fetched and updated weeklyPrompts in context with', promptsToSet.length, 'prompts');
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (error) {
            console.error('❌ Error fetching weekly prompts:', error);
          }
        }
      } else {
        console.warn('⚠️ setWeeklyPrompts not available in context');
      }
      
      // Log final state before navigation
      console.log('📊 Final state before navigation:', {
        promptsToSetCount: promptsToSet.length,
        promptsToSet: promptsToSet,
        weekStart: result?.weekStart
      });

      // Dismiss weekly planner to prevent redirect loop
      if (dismissWeeklyPlanner) {
        dismissWeeklyPlanner();
      }

      // Navigate back to prompt library with state to switch to weekly view
      // Include weekStart in state to avoid week mismatch when reloading
      console.log('🧭 Navigating to /home with switchToWeekly state');
      navigate('/home', { 
        state: { 
          switchToWeekly: true,
          weekStart: result?.weekStart
        },
        replace: false
      });
    } catch (error) {
      console.error('❌ Error saving weekly prompts:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      alert(`Failed to save weekly prompts: ${error.message}`);
    }
  };

  const handleClose = () => {
    // Mark as dismissed so it doesn't reopen automatically
    if (dismissWeeklyPlanner) {
      dismissWeeklyPlanner();
    }
    navigate('/home');
  };

  const currentActivity = useMemo(() => {
    if (selectedActivities.length > 0 && currentActivityIndex < selectedActivities.length) {
      const activityId = selectedActivities[currentActivityIndex];
      for (const bucket of activityTypes) {
        const activity = bucket.activities.find(a => a.activityTypeId === activityId);
        if (activity) {
          return { ...activity, contentBucket: bucket.contentBucket };
        }
      }
    }
    return null;
  }, [selectedActivities, currentActivityIndex, activityTypes]);

  const currentActivityPromptIds = currentActivity
    ? (selectedPromptsByActivity[currentActivity.activityTypeId] || [])
    : [];

  return (
    <div className="weekly-planner-screen">
      <div className="weekly-planner-screen-container">
        <div className="weekly-planner-screen-gradient-header"></div>
        <div className="weekly-planner-screen-content">
          <button className="weekly-planner-screen-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
          
          {loading ? (
            <div className="weekly-planner-screen-loading">Loading...</div>
          ) : currentScreen === SCREEN_ACTIVITY_SELECTION ? (
            <ActivitySelectionScreen
              activityTypes={activityTypes}
              selectedActivities={selectedActivities}
              onActivityToggle={handleActivityToggle}
              onNext={handleActivityNext}
            />
          ) : currentScreen === SCREEN_PROMPT_SELECTION && currentActivity ? (
            <PromptSelectionScreen
              activity={currentActivity}
              activityIndex={currentActivityIndex}
              totalActivities={selectedActivities.length}
              selectedPromptIds={currentActivityPromptIds}
              onPromptToggle={handlePromptToggle}
              onNext={handlePromptNext}
              onBack={handlePromptBack}
              organizationId={organization?.id || organization?.orgId}
              userId={user?.id || user?.userId}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default WeeklyPlannerScreen;

